import React, { MutableRefObject, useEffect, useState } from 'react';
import Form from '@rjsf/core';
import styled from '@emotion/styled';
import { Box, CardHeader, darken, Divider, Grid, lighten } from '@mui/material';
import { RJSFSchema } from '@rjsf/utils';
import { useAppSelector } from '@/redux/hooks';
import { CustomerProfileData, UserProfile, type CustomerProfile, type JobDraft } from '@/redux/slices/slice_types';
import { selectCustomersList } from '@/redux/selectors/constantsSelectors';
import { selectProfiles, selectWorkflows } from '@/redux/selectors/profileSelectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import PolarisIcon from '@/components/_common/PolarisIcon';
import Autocomplete from '@/components/_common/Form/Autocomplete';
import Datepicker from '@/components/_common/Datepicker/Datepicker';
import ProfileSelectionField from '../OPStep1OrderDetails/ProfileSelectionField';
import type { ProfileDetailsOutputProfile } from '../OPStep1OrderDetails/OPProfileDetailsOutput';
import OPProfileDetailsOutput from '../OPStep1OrderDetails/OPProfileDetailsOutput';
import { selectSelectedHierarchy } from '@/redux/selectors/orderPlacementSelectors';
import JobOrderProfileForm, { BaseProps as OrderProfileFormBaseProps } from './JobOrderProfileForm';
import { isAfter } from 'date-fns';
import { OrderPlacementConfig } from '@/__configs/config_types';
import EmbargoDatepicker from '@/components/_common/EmbargoDatepicker';
import useComponentOverride from '@/components/_hooks/useComponentOverride';

export interface ProfileDetailsFormProfile {
  id: number;
  name: string;
  label: string;
  data: ProfileDetailsOutputProfile['data'] & {
    workflow_type?: CustomerProfileData['workflow_type'];
    template_inputs?: CustomerProfileData['template_inputs'];
  };
}

export type ProfileDetailsFormJob = Partial<
  Pick<JobDraft, 'customer' | 'due_date' | 'embargo_date' | 'order_profile_inputs' | 'order_spec_inputs'>
> & {
  profile?: ProfileDetailsFormProfile | CustomerProfile | null;
  assignee_user_id?: UserProfile | string | null;
};

export type ProfileDetailsFormFieldChange =
  | { key: 'customer'; value: ProfileDetailsFormJob['customer'] }
  | { key: 'profile'; value: ProfileDetailsFormJob['profile'] }
  | { key: 'due_date'; value: ProfileDetailsFormJob['due_date'] }
  | { key: 'embargo_date'; value: ProfileDetailsFormJob['embargo_date'] }
  | { key: 'order_profile_inputs'; value: NonNullable<ProfileDetailsFormJob['order_profile_inputs']> }
  | { key: 'assignee_user_id'; value: NonNullable<ProfileDetailsFormJob['assignee_user_id']> };
export type ProfileDetailsFormHandleChange = (change: ProfileDetailsFormFieldChange) => void;

interface OrderSpecFormProps {
  schema?: RJSFSchema;
  inputs?: Record<string, any>;
  beforeMessage?: React.ReactNode;
}

type OrderProfileFormProps = {
  formRef?: MutableRefObject<Form | null>;
  schema?: RJSFSchema;
  inputs?: Record<string, any>;
  beforeMessage?: React.ReactNode;
} & ({ register: false } | { register: true; jobId: number }); // If we want to register a form, we need to provide `jobId`. That's why there is a discriminated union in the Props definition

interface Props {
  job: ProfileDetailsFormJob;
  orderSpecForm: OrderSpecFormProps;
  orderProfileForm: OrderProfileFormProps;
  validationErrors?: {
    customer?: boolean;
    profile?: boolean;
    due_date?: boolean;
  };
  additionalFields?: React.ReactNode;
  isInsideModal?: boolean;
  handleChange: ProfileDetailsFormHandleChange;
  updateOrderSpecInputs: (inputs: Record<string, any> | undefined) => void;
}

const OPJobProfileDetailsForm = (props: Props) => {
  const {
    job,
    orderSpecForm,
    orderProfileForm,
    validationErrors,
    additionalFields,
    isInsideModal = false,
    handleChange,
    updateOrderSpecInputs
  } = props;
  const { customer = null, profile = null, due_date: dueDate, embargo_date: embargoDate } = job;
  const customersListFromStore = useAppSelector(selectCustomersList);
  const profiles: { [key: string]: CustomerProfile[] } = useAppSelector(selectProfiles);
  const workflows = useAppSelector(selectWorkflows);
  const selectedHierarchy = useAppSelector(selectSelectedHierarchy);
  const workflowName = profile?.data?.workflow && workflows[profile?.data?.workflow] ? profile?.data?.workflow : undefined;

  const disableDueDate = useConfigValue(['orderPlacementConfig', 'disableDueDate']);
  const disableEmbargoDate = useConfigValue(['orderPlacementConfig', 'disableEmbargoDate']);
  const filterProfileList = useConfigValue(['orderPlacementConfig', 'filterProfileList']) as OrderPlacementConfig['filterProfileList'] | undefined;
  const filterCustomerList = useConfigValue(['orderPlacementConfig', 'filterCustomerList']) as OrderPlacementConfig['filterCustomerList'] | undefined;
  const isOrderProfileSchemaFormVisible = !!Object.keys(orderProfileForm.schema?.properties ?? {}).length;
  const [prevProfile, setPrevProfile] = useState<CustomerProfileData['id'] | undefined>(undefined);

  const [profileOptions, setProfileOptions] = useState<CustomerProfile[]>([]);
  const [customersList, setCustomersList] = useState<string[]>([]);

  useEffect(() => {
    if (filterCustomerList && selectedHierarchy) {
      const filteredCustomers = filterCustomerList(customersListFromStore, selectedHierarchy);

      setCustomersList(filteredCustomers);
    } else {
      setCustomersList(customersListFromStore);
    }
  }, [customersListFromStore]);

  useEffect(() => {
    if (customer) {
      if (profiles?.[customer]) {
        const newProfiles = profiles[customer].filter(
          (p) => p.data.workflow_type && ['version_based', 'package', 'version_based_bundle'].indexOf(p.data.workflow_type) !== -1
        );

        if (filterProfileList && selectedHierarchy) {
          const filteredProfiles = filterProfileList(profiles, customer, selectedHierarchy);

          setProfileOptions(filteredProfiles);
        } else {
          setProfileOptions(newProfiles);
        }
      }
    }
  }, [customer, profiles]);

  useEffect(() => {
    if (prevProfile !== profile?.id) setTimeout(() => setPrevProfile(profile?.id), 50);
  }, [profile]);

  const dueDateParsed = dueDate ? new Date(dueDate) : undefined;
  const embargoDateParsed = embargoDate ? new Date(embargoDate) : undefined;
  const workflowVersion = workflowName && workflows[workflowName].workflow_version;
  const hasOrderSpecSchemaProperties = !!Object.keys(orderSpecForm.schema?.properties ?? {}).length;

  const orderProfileFormBaseProps: OrderProfileFormBaseProps = {
    formRef: orderProfileForm.formRef,
    schema: orderProfileForm.schema,
    inputs: orderProfileForm.inputs,
    handleChange: handleChange
  };

  const handleDueDateChange = (date: Date | null) => {
    if (embargoDateParsed && isAfter(embargoDateParsed, date as Date)) {
      handleChange({ key: 'due_date', value: date ? date.toISOString() : undefined });
      handleChange({ key: 'embargo_date', value: undefined });
    } else {
      handleChange({ key: 'due_date', value: date ? date.toISOString() : undefined });
    }
  };

  return (
    <Grid container columnSpacing={2}>
      <Grid item sm={10} md={8} sx={{ p: 1 }}>
        <CardHeader title="Profile Details" />
        <Box sx={{ p: 4 }}>
          <div>
            <Grid container spacing={5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={5} lg={4}>
                <Autocomplete
                  label="Customer"
                  required
                  options={customersList}
                  value={customer}
                  onChange={(_, value) => handleChange({ key: 'customer', value })}
                  disabled={customersList?.length === 1}
                  error={validationErrors?.customer}
                />
              </Grid>

              <Grid item xs={12} md={7}>
                <ProfileSelectionField
                  value={profile}
                  options={profileOptions}
                  onChange={(_, value) => handleChange({ key: 'profile', value })}
                  error={validationErrors?.profile}
                />
              </Grid>
            </Grid>

            <Grid container spacing={5}>
              <Grid item xs={12} md={6} lg={4}>
                <Datepicker
                  label="Due Date"
                  required={!disableDueDate}
                  selected={disableDueDate ? undefined : dueDateParsed}
                  showTimeSelect
                  onChange={handleDueDateChange}
                  minDate={new Date()}
                  error={validationErrors?.due_date}
                  disabled={!!disableDueDate}
                  helperText={disableDueDate ? 'Externally managed' : undefined}
                  isInsideModal={isInsideModal}
                />
              </Grid>
              {!disableEmbargoDate && (
                <Grid item xs={12} md={6} lg={4}>
                  <EmbargoDatepicker
                    selected={embargoDateParsed}
                    dueDate={dueDateParsed}
                    isInsideModal={isInsideModal}
                    onChange={(date) => handleChange({ key: 'embargo_date', value: date ? date.toISOString() : undefined })}
                  />
                </Grid>
              )}
              <OPJobProfileDetailsFormCustomFields job={job} handleChange={handleChange} />
              {additionalFields}
              {isOrderProfileSchemaFormVisible && prevProfile === profile?.id && (
                <Grid item xs={11}>
                  <Divider sx={{ mb: 3, mt: -1 }} />
                  {orderProfileForm.beforeMessage}
                  {orderProfileForm.register ? (
                    <JobOrderProfileForm {...orderProfileFormBaseProps} register jobId={orderProfileForm.jobId} />
                  ) : (
                    <JobOrderProfileForm {...orderProfileFormBaseProps} register={false} />
                  )}
                </Grid>
              )}
            </Grid>
          </div>
        </Box>
      </Grid>
      <Grid item sm={12} md={4}>
        <OutputContainer>
          <Grid container sx={{ height: '100%' }}>
            {profile?.data?.workflow_type &&
            ['version_based', 'package', 'version_based_bundle'].indexOf(profile.data.workflow_type) !== -1 &&
            (!workflowVersion || workflowVersion !== 'v2' || (workflowVersion === 'v2' && hasOrderSpecSchemaProperties)) ? (
              <OPProfileDetailsOutput
                profile={profile}
                orderSpecSchema={orderSpecForm.schema}
                orderSpecInputs={orderSpecForm.inputs}
                orderSpecSchemaFormBeforeMessage={orderSpecForm.beforeMessage}
                updateOrderSpecInputs={updateOrderSpecInputs}
                onSave={(data: CustomerProfile['data']) => handleChange({ key: 'profile', value: { ...profile, data } })}
              />
            ) : (
              <Grid item container alignItems="center" justifyContent="center">
                <PolarisIcon icon="files" width={120} style={{ opacity: 0.2 }} />
              </Grid>
            )}
          </Grid>
        </OutputContainer>
      </Grid>
    </Grid>
  );
};

export type OPJobProfileDetailsFormCustomFieldsProps = {
  handleChange: ProfileDetailsFormHandleChange;
  job: ProfileDetailsFormJob;
};

const OPJobProfileDetailsFormCustomFields = (props: OPJobProfileDetailsFormCustomFieldsProps) =>
  useComponentOverride<OPJobProfileDetailsFormCustomFieldsProps>(() => null, 'orderPlacementJobProfileDetailsFormCustomFields', props);

export default OPJobProfileDetailsForm;

const OutputContainer = styled.div`
  position: relative;
  height: 100%;
  ${({ theme }) => theme.palette.mode === 'light' && `border-left: 1px solid ${theme.palette.divider};`}
  background: ${({ theme }) =>
    theme.palette.mode === 'light' ? darken(theme.palette.background.default, 0.03) : lighten(theme.palette.background.paper, 0.05)};
`;
